import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../lib/firebase';

export interface UsageSummary {
  id: string;
  user_id: string;
  api_key_id: string;
  billing_period_start: string;
  billing_period_end: string;
  total_requests: number;
  successful_requests: number;
  failed_requests: number;
  total_cost_usd: number;
  total_response_time_ms: number;
  created_at: string;
  updated_at: string;
}

export interface UsageLog {
  id: string;
  endpoint: string;
  method: string;
  status_code: number;
  response_time_ms: number;
  request_size_bytes: number;
  response_size_bytes: number;
  cost_usd: number;
  created_at: string;
  metadata: any;
}

export const getUserUsageSummary = async (
  userId: string,
  startDate?: string,
  endDate?: string
): Promise<UsageSummary[]> => {
  const snapshot = await getDocs(query(collection(db, 'api_usage_summary'), where('user_id', '==', userId)));
  const rows = snapshot.docs.map((item) => ({ id: item.id, ...(item.data() as Omit<UsageSummary, 'id'>) }));

  return rows
    .filter((row) => !startDate || row.billing_period_start >= startDate)
    .filter((row) => !endDate || row.billing_period_end <= endDate)
    .sort((a, b) => new Date(b.billing_period_start).getTime() - new Date(a.billing_period_start).getTime());
};

export const getUserUsageLogs = async (
  userId: string,
  limit: number = 100,
  offset: number = 0,
  startDate?: string,
  endDate?: string
): Promise<UsageLog[]> => {
  const snapshot = await getDocs(query(collection(db, 'api_usage_logs'), where('user_id', '==', userId)));
  const rows = snapshot.docs.map((item) => ({ id: item.id, ...(item.data() as Omit<UsageLog, 'id'>) }));

  const filtered = rows
    .filter((row) => !startDate || row.created_at >= startDate)
    .filter((row) => !endDate || row.created_at <= endDate)
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  return filtered.slice(offset, offset + limit);
};

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 3,
  }).format(amount);
};

export const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString();
};

export const formatDateTime = (dateString: string): string => {
  return new Date(dateString).toLocaleString();
};

export const getStatusColor = (statusCode: number): string => {
  if (statusCode >= 200 && statusCode < 300) return '#10b981';
  if (statusCode >= 400 && statusCode < 500) return '#f59e0b';
  return '#ef4444';
};
